/**
 * Prodigal son coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_bible_stories`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const prodigalSonColoringPagesPack: ToilePack = {
    slug: 'prodigal-son-coloring-pages',
    category: 'faith',
    name: 'Prodigal son coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'leaving', label: 'Leaving home', subject: "the younger son walking away from his father's farmhouse with a bag of money, his father watching from the door"},
        {key: 'pigs', label: 'With the pigs', subject: 'the son sitting hungry in a field feeding pigs, wishing for their pods'},
        {key: 'return', label: 'The return', subject: 'the father running down the road to embrace his returning son, a servant behind bringing a robe and a ring'},
    ],
    styles: [
        {key: 'bold', label: 'Bold and simple', style: 'bold thick-outline coloring book style, one heavy uniform black line, big simple open shapes for young children', palette: 'black ink only', framing: 'The drawing centered with a wide white margin on all sides'},
        {key: 'classic', label: 'Classic', style: 'classic coloring book line art, medium uniform black line, moderate detail and open shapes for ages six to nine', palette: 'black ink only', framing: 'The full page used, the drawing centered with a white margin'},
        {key: 'detailed', label: 'Fine detail', style: 'fine-line detailed coloring page, thin uniform black line, patterned robes, leaves and waves for older children', palette: 'black ink only', framing: 'The scene held inside a plain thin rectangular border with a white margin outside it'},
    ],
    directions: ['big feast', 'old father', 'fatted calf'],
    directionHint: 'try: big feast',
    seo: {
        title: 'Prodigal Son Coloring Pages: Luke 15 in Three Scenes',
        metaDescription: "Nine prodigal son coloring pages for Sunday school from Luke 15: leaving home, feeding pigs and the father's return, in three line styles.",
        h1: 'Prodigal son coloring pages: leaving home, with the pigs, the return',
        tagline: 'Nine pages of the parable of the prodigal son in three scenes, each in three line weights.',
        intro: "Printable prodigal son coloring pages for Sunday school and Christian homeschool, the parable in Luke 15. Three scenes: the younger son leaving his father's farmhouse with a bag of money, the son feeding pigs in a field, and the father running down the road to embrace him. Bold, classic and fine-detail lines. Type a different scene, and the same styles carry it.",
        useCases: [
            'a Sunday school lesson on Luke 15',
            "a children's church craft table",
            'a Christian homeschool Gospel unit',
            'a bulletin board on forgiveness',
        ],
        faq: [
            {q: 'Which part of the parable do these show?', a: "Leaving home, the son feeding pigs, and the father's welcome."},
            {q: 'Can I ask for the feast or the fatted calf?', a: 'Yes. Type the scene you want. The request shapes the page, but a result is not promised to match it exactly.'},
            {q: 'Which style suits a young child?', a: 'Bold and simple, with one heavy line and big open shapes.'},
            {q: 'Are the pages in color?', a: 'No. Each page is black line on white, with open shapes to color in.'},
        ],
    },
}
